class FPProductSalon extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.variants = JSON.parse(this.querySelector('[data-fp-variants]')?.textContent || '[]');
    this.sizeProfile = JSON.parse(this.querySelector('[data-size-guide-data]')?.textContent || '{}').profile;
    this.isPriceOnRequest = this.dataset.priceOnRequest === 'true';
    this.colorIndex = Number(this.dataset.colorPosition || 0) - 1;
    this.sizeIndex = Number(this.dataset.sizePosition || 0) - 1;
    this.form = this.querySelector('form');
    this.addButton = this.querySelector('[ref="addToCartButton"]');
    this.optionInputs = [...this.querySelectorAll('[data-option-position] input[type="radio"]')];
    this.gallery = this.querySelector('fp-pdp-gallery');
    this.quantityInput = this.querySelector('[data-fp-quantity]');
    this.quantityMinusButton = this.querySelector('[data-fp-quantity-minus]');
    this.quantityPlusButton = this.querySelector('[data-fp-quantity-plus]');
    this.stickyAdd = this.querySelector('[data-fp-sticky-add]');
    this.primaryButtonPassed = false;

    this.optionInputs.forEach((input) => input.addEventListener('change', (event) => this.onOptionChange(event)));
    this.quantityMinusButton?.addEventListener('click', () => this.changeQuantity(-1));
    this.quantityPlusButton?.addEventListener('click', () => this.changeQuantity(1));
    this.quantityInput?.addEventListener('input', () => this.normalizeQuantity());
    this.stickyAdd?.addEventListener('click', () => this.form?.requestSubmit());
    // Buy it now: add the chosen piece (with its US size property) and go straight to checkout
    this.querySelector('[data-fp-buy-now]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget; const error = this.querySelector('[data-fp-buy-error]');
      if (!this.form || button.disabled) return;
      button.disabled = true; button.setAttribute('aria-busy', 'true'); if (error) error.hidden = true;
      try {
        const body = new FormData(this.form);
        // the US size line property comes from the same variant map "Add to bag" uses (assets/product-form.js)
        const sizeMap = JSON.parse(this.form.querySelector('[data-fp-size-variants]')?.textContent || '{}');
        const usSize = sizeMap[body.get('id')];
        if (usSize) body.set('properties[US size]', usSize); else body.delete('properties[US size]');
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'This piece could not be added.');
        }
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      } catch (problem) {
        if (error) { error.textContent = problem.message; error.hidden = false; }
        button.disabled = false; button.removeAttribute('aria-busy');
      }
    });
    // share: the phone's share sheet where there is one, otherwise the link is copied
    this.querySelector('[data-fp-share]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget; const label = button.querySelector('[data-fp-share-label]');
      const url = location.href.split('#')[0];
      try {
        if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title: button.dataset.title, url }); return; }
        await navigator.clipboard.writeText(url);
        if (label) { label.textContent = 'Link copied'; setTimeout(() => { label.textContent = 'Share'; }, 2200); }
      } catch (_) { /* dismissed */ }
    });
    // a colour's first photo starts loading when the visitor points at (or focuses) its swatch
    this.querySelectorAll('[data-fp-color-value]').forEach((value) => {
      const warm = () => this.gallery?.prewarm?.(value.dataset.fpColorValue);
      value.addEventListener('pointerenter', warm); value.addEventListener('focusin', warm);
    });
    this.animateDisclosures();
    document.addEventListener('toggle', () => this.syncStickyVisibility(), true);
    // the details column stays in view beside the photos; a column taller than the screen (accordions open)
    // scrolls until its end shows, then holds there
    const info = this.querySelector('.fp-pdp__info-inner');
    if (info && 'ResizeObserver' in window) {
      const setTop = () => {
        const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
        this.style.setProperty('--fp-pdp-info-top', `${Math.min(header + 24, innerHeight - info.offsetHeight - 24)}px`);
      };
      new ResizeObserver(setTop).observe(info);
      addEventListener('resize', setTop, { passive: true });
    }

    this.userChanged = false;
    this.update(this.variantFromId(this.querySelector('[data-fp-variant-id]')?.value));
    this.normalizeQuantity();
    this.observePrimaryButton();
  }

  changeQuantity(change) {
    if (!this.quantityInput) return;
    const current = Number.parseInt(this.quantityInput.value, 10) || 1;
    this.quantityInput.value = String(current + change);
    this.normalizeQuantity();
  }

  normalizeQuantity() {
    if (!this.quantityInput) return;
    const minimum = Number.parseInt(this.quantityInput.min, 10) || 1;
    const maximum = Number.parseInt(this.quantityInput.max, 10) || 99;
    const value = Math.min(maximum, Math.max(minimum, Number.parseInt(this.quantityInput.value, 10) || minimum));
    this.quantityInput.value = String(value);
    if (this.quantityMinusButton) this.quantityMinusButton.disabled = value <= minimum;
    if (this.quantityPlusButton) this.quantityPlusButton.disabled = value >= maximum;
  }

  variantFromId(id) {
    return this.variants.find((variant) => String(variant.id) === String(id));
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
  }

  findExactVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  onOptionChange(event) {
    this.userChanged = true;
    let options = this.selectedOptions();
    let variant = this.findExactVariant(options);

    const fieldset = event.target.closest('[data-option-position]');
    if ((!variant || !variant.available) && fieldset?.hasAttribute('data-color-option') && this.colorIndex >= 0) {
      variant = this.variants.find((candidate) => candidate.available && candidate.options[this.colorIndex] === event.target.value);
      if (variant) {
        this.syncInputs(variant.options);
        options = variant.options;
      }
    }

    this.update(variant || this.findExactVariant(options));
  }

  syncInputs(options) {
    [...this.querySelectorAll('[data-option-position]')].forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionPosition) - 1;
      const input = [...fieldset.querySelectorAll('input')].find((candidate) => candidate.value === options[index]);
      if (input) input.checked = true;
    });
  }

  update(variant) {
    if (!variant) return;

    const variantInput = this.querySelector('[data-fp-variant-id]');
    if (variantInput) variantInput.value = variant.id;
    const selectedSize = this.sizeProfile?.sizeMap?.find(row => row.native === variant.options[this.sizeIndex]);
    let usProperty = this.form?.querySelector('[name="properties[US size]"]');
    if (selectedSize?.us && this.form) {
      if (!usProperty) { usProperty = document.createElement('input'); usProperty.type = 'hidden'; usProperty.name = 'properties[US size]'; this.form.appendChild(usProperty); }
      usProperty.value = selectedSize.us;
    } else usProperty?.remove();

    const price = this.querySelector('[data-fp-price]');
    const compare = this.querySelector('[data-fp-compare]');
    const availability = this.querySelector('[data-fp-availability]');
    const stickyPrice = this.querySelector('[data-fp-sticky-price]');
    const stickyVariant = this.querySelector('[data-fp-sticky-variant]');
    const stickyLabel = this.querySelector('[data-fp-sticky-label]');
    const stickyImage = this.querySelector('.fp-pdp__dock-image');
    if (price) price.textContent = this.isPriceOnRequest ? 'Price on request' : variant.price;
    if (stickyPrice) stickyPrice.textContent = this.isPriceOnRequest ? 'Price on request' : variant.price;
    if (compare) {
      compare.textContent = variant.compareAt;
      compare.hidden = !variant.compareAt || variant.compareAt === variant.price;
    }
    if (availability) {
      availability.lastChild.textContent = this.isPriceOnRequest ? 'Available by inquiry' : (variant.available ? 'In stock' : (this.sizeIndex >= 0 ? 'Sold out in this size' : 'Sold out'));
      availability.classList.toggle('is-unavailable', !variant.available && !this.isPriceOnRequest);
    }
    const buyNow = this.querySelector('[data-fp-buy-now]');
    if (buyNow) buyNow.hidden = !variant.available;
    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      const textNode = [...this.addButton.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNode) textNode.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
    if (stickyVariant) {
      const optionLabel = variant.options.map((value, index) => index === this.sizeIndex ? (selectedSize?.display || value) : value).filter((value) => value && value !== 'Default Title').join(' / ');
      stickyVariant.textContent = optionLabel;
      stickyVariant.hidden = !optionLabel;
    }
    if (stickyImage && variant.image) stickyImage.src = variant.image;
    if (this.stickyAdd) this.stickyAdd.disabled = !variant.available;
    if (stickyLabel) stickyLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
    this.syncStickyVisibility();

    const selectedColor = this.colorIndex >= 0 ? variant.options[this.colorIndex] : '';
    const selectedColorLabel = this.querySelector('[data-fp-selected-color]');
    if (selectedColorLabel) selectedColorLabel.textContent = selectedColor;
    this.updateSizeAvailability(selectedColor);
    this.updateMedia(selectedColor);

    const url = new URL(this.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', `${url.pathname}${url.search}`);
  }

  updateSizeAvailability(selectedColor) {
    const sizeFieldset = this.querySelector('[data-size-option]');
    if (!sizeFieldset || this.sizeIndex < 0) return;

    sizeFieldset.querySelectorAll('input').forEach((input) => {
      const available = this.variants.some((variant) => {
        const matchesColor = this.colorIndex < 0 || variant.options[this.colorIndex] === selectedColor;
        return matchesColor && variant.options[this.sizeIndex] === input.value && variant.available;
      });
      input.disabled = !available;
      input.setAttribute('aria-disabled', String(!available));
      input.closest('[data-fp-option-value]')?.classList.toggle('is-unavailable', !available);
    });
  }

  updateMedia(selectedColor) {
    if (!this.gallery) return;
    const animate = this.userChanged;
    customElements.whenDefined('fp-pdp-gallery').then(() => this.gallery.showColour(selectedColor, { animate }));
  }

  // accordions open and close smoothly (CSS ::details-content where supported, otherwise a height animation)
  animateDisclosures() {
    if (CSS.supports?.('selector(::details-content)') || matchMedia('(prefers-reduced-motion: reduce)').matches || !Element.prototype.animate) return;
    this.querySelectorAll('.fp-pdp__disclosures details').forEach((d) => {
      const summary = d.querySelector('summary'); const body = d.querySelector('.fp-pdp__disclosure-body');
      if (!summary || !body) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d.dataset.busy) return; d.dataset.busy = '1';
        const opening = !d.open;
        if (opening) d.open = true;
        const h = body.scrollHeight;
        const anim = body.animate([{ height: opening ? '0px' : `${h}px`, opacity: opening ? 0 : 1 }, { height: opening ? `${h}px` : '0px', opacity: opening ? 1 : 0 }], { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
        body.style.overflow = 'hidden';
        anim.onfinish = () => { if (!opening) d.open = false; body.style.overflow = ''; delete d.dataset.busy; };
      });
    });
  }

  observePrimaryButton() {
    if (!this.stickyAdd || !this.addButton || !('IntersectionObserver' in window)) return;
    // the button counts as gone once it has slid under the sticky header, not only past the top of the screen
    const header = Math.round(parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0);
    const observer = new IntersectionObserver(([entry]) => {
      this.primaryButtonPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= header + 1;
      this.syncStickyVisibility();
    }, { rootMargin: `-${header}px 0px 0px 0px` });
    observer.observe(this.addButton);
  }

  syncStickyVisibility() {
    if (!this.stickyAdd) return;
    const cartDialog = document.querySelector('#cart-drawer dialog');
    const cartOpen = Boolean(cartDialog?.open || cartDialog?.matches('[open]'));
    const show = this.primaryButtonPassed && !this.addButton?.disabled && !cartOpen;
    this.stickyAdd.classList.toggle('is-shown', show);
    this.stickyAdd.inert = !show;
  }
}

if (!customElements.get('fp-house-product-salon')) {
  customElements.define('fp-house-product-salon', FPProductSalon);
}
